import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Truck, Save, Info } from 'lucide-react'
import { apiGet, apiPatch } from '../lib/api'

/**
 * Configuración de costos de envío — autoadministrable.
 * 3 reglas planas (replicadas del proyecto Inertia):
 *  1. Monto mínimo para envío gratis
 *  2. Costo de envío estándar
 *  3. Costo especial para productos de categoría Bedliner
 *
 * Editable desde aquí. Los cambios afectan inmediatamente al cálculo de envío
 * del checkout (cliente público + admin pedidos).
 */

interface ShippingSetting {
  key: string
  label: string
  value: number
  description: string
  updatedAt: string | null
}

const KEY_META: Record<string, { hint: string; example: string }> = {
  free_shipping_threshold: {
    hint: 'Si el subtotal del carrito es ≥ a este monto, el envío es gratis (excepto productos Bedliner).',
    example: 'Ej. 499 → todas las compras de $499 o más tienen envío gratis.',
  },
  standard_shipping_cost: {
    hint: 'Costo que se cobra cuando NO aplica envío gratis ni Bedliner.',
    example: 'Ej. 220 → si el subtotal es menor al mínimo, se cobra $220 de envío.',
  },
  bedliner_shipping_cost: {
    hint: 'Costo especial cuando el carrito tiene al menos un producto de la categoría Bedliner. NO aplica envío gratis.',
    example: 'Ej. 2500 → cualquier carrito con Bedliner cobra $2,500 de envío sin importar el subtotal.',
  },
}

export function Envios() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{ items: ShippingSetting[] }>({
    queryKey: ['shipping-settings'],
    queryFn: () => apiGet('/api/admin/shipping-settings'),
  })

  const [edited, setEdited] = useState<Record<string, number>>({})
  const [saved, setSaved] = useState<string[]>([])

  useEffect(() => {
    if (data?.items) {
      const initial: Record<string, number> = {}
      for (const it of data.items) initial[it.key] = it.value
      setEdited(initial)
    }
  }, [data])

  const saveMut = useMutation({
    mutationFn: (items: Array<{ key: string; value: number }>) =>
      apiPatch('/api/admin/shipping-settings', { items }),
    onSuccess: (_d, vars) => {
      setSaved(vars.map((v) => v.key))
      setTimeout(() => setSaved([]), 3000)
      qc.invalidateQueries({ queryKey: ['shipping-settings'] })
    },
  })

  function onSave(key: string) {
    const value = Number(edited[key] ?? 0)
    if (!isFinite(value) || value < 0) return
    saveMut.mutate([{ key, value }])
  }

  function onSaveAll() {
    const items = Object.entries(edited).map(([key, value]) => ({ key, value: Number(value) }))
    saveMut.mutate(items)
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="grid size-11 place-items-center rounded-lg bg-brand-50 text-brand-600">
          <Truck className="size-5" strokeWidth={2.2} />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-ink-900">Costos de envío</h1>
          <p className="text-sm text-ink-500">
            Reglas planas: envío gratis sobre cierto monto + costo estándar + costo Bedliner especial.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-xl border border-ink-200 bg-white p-8 text-center text-ink-500">
          Cargando…
        </div>
      ) : (
        <div className="space-y-4">
          {(data?.items || []).map((s) => {
            const meta = KEY_META[s.key]
            const isSaved = saved.includes(s.key)

            return (
              <div key={s.key} className="rounded-xl border border-ink-200 bg-white p-5">
                <div className="mb-3">
                  <h3 className="text-base font-bold text-ink-900">{s.label}</h3>
                  {meta && (
                    <p className="mt-1 flex items-start gap-1.5 text-xs text-ink-500">
                      <Info className="mt-0.5 size-3.5 shrink-0" />
                      <span>{meta.hint}</span>
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative max-w-xs flex-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink-500">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={edited[s.key] ?? s.value}
                      onChange={(e) =>
                        setEdited((p) => ({ ...p, [s.key]: Number(e.target.value) }))
                      }
                      className="block w-full rounded-lg border border-ink-200 bg-white py-2.5 pl-7 pr-12 text-right text-sm font-semibold tabular-nums focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-100"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-400">
                      MXN
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => onSave(s.key)}
                    disabled={saveMut.isPending || edited[s.key] === s.value}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2.5 text-xs font-bold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Save className="size-3.5" />
                    Guardar
                  </button>

                  {isSaved && (
                    <span className="text-xs font-semibold text-green-600">✓ Guardado</span>
                  )}
                </div>

                {meta && (
                  <p className="mt-3 text-xs italic text-ink-400">{meta.example}</p>
                )}
              </div>
            )
          })}

          <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            <span>
              💡 Los cambios se aplican <strong>inmediatamente</strong>. No requiere deploy ni reinicio.
            </span>
            <button
              type="button"
              onClick={onSaveAll}
              disabled={saveMut.isPending}
              className="rounded bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              Guardar todo
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
